<script lang="ts">
  /**
   * Renforcer un équipement — le geste, pas l'objet.
   *
   * Il vivait sous chaque ligne du sac, ce qui le rendait invisible tant qu'on
   * n'avait pas déplié la bonne, et proposait tout à tout le monde : un
   * Technodiacre y voyait les Cercles de l'Alchimiste, grisés, sans jamais
   * pouvoir s'en servir.
   *
   * # La question posée : qui est à l'origine du renforcement ?
   *
   * **Moi.** Seules les voies que ma Classe et mon sac ouvrent, et rien
   * d'autre — ce que je ne peux pas faire n'a pas à s'afficher. Ça consomme
   * mon Matériel, et ça s'inscrit sur ma pièce.
   *
   * **Un autre.** L'Alchimiste de la bande trace son Cercle sur mon épée
   * (Codex XI p.232), l'artisan du village fait le Test à son établi. Tout
   * est proposé — ce sont ses Capacités, et je ne les connais pas — et **rien
   * ne part de mon sac** : ce qui part, part du sien. Le résultat s'inscrit
   * sur ma pièce, puisque c'est elle qui en sort renforcée.
   *
   * Dans les deux cas le livre garde son mot sur la pièce elle-même : une
   * Propriété qu'elle porte déjà, un Rituel qu'elle ne cumule pas, restent
   * refusés — c'est l'objet qui refuse, pas le praticien.
   */
  import { renfortsPossibles, sac as lireLeSac, type PersonnageEnregistre } from '../../../rules'

  let {
    personnage,
    renforcer,
    ameliorer,
    attribuer,
    monter,
  }: {
    personnage: PersonnageEnregistre
    /** Pose un Renforcement sur un exemplaire, ou l'en retire. Ne consomme rien. */
    renforcer: (objet: string, source: string, sens: number) => void
    /** Tente une amélioration d'Artisanat : mon Matériel part, réussi ou non. */
    ameliorer: (objet: string, propriete: string, reussi: boolean) => void
    /** Attribue une Propriété sans rien consommer : le Matériel était à l'autre. */
    attribuer: (objet: string, propriete: string) => void
    /** Monte une Amélioration de Veredium, ou la démonte. Ne consomme rien. */
    monter: (objet: string, amelioration: string) => void
  } = $props()

  /** Qui est à l'origine du renforcement. */
  let origine = $state<'moi' | 'autre'>('moi')

  /* Seules les armes et les armures se renforcent : le livre ne dit rien
     d'une corde ni d'une fiole. */
  const pieces = $derived(
    lireLeSac(personnage).lignes.filter((l) => l.genre === 'arme' || l.genre === 'armure'),
  )
  let choisi = $state('')
  const cible = $derived(choisi || (pieces[0]?.id ?? ''))
  const piece = $derived(pieces.find((l) => l.id === cible))

  /* Le moteur rend tout, avec ce qui ferme chaque voie pour moi. C'est ici
     qu'on décide quoi en montrer : pour moi, seulement l'ouvert ; pour un
     autre, tout — mais toujours sans ce que l'objet lui-même refuse. */
  const voies = $derived.by(() => {
    if (cible === '') return []
    const toutes = renfortsPossibles(personnage, cible)
    if (origine === 'autre') return toutes
    return toutes
      .filter((v) => v.ouverte)
      .map((v) => ({ ...v, offres: v.offres.filter((g) => g.refus === null) }))
      .filter((v) => v.offres.length > 0)
  })
</script>

<div class="cartouche">
  <div class="bandeau">Renforcer un équipement</div>
  <div class="corps">
    {#if pieces.length === 0}
      <p class="vide">Aucune arme ni armure au sac.</p>
    {:else}
      <select class="saisie" bind:value={choisi} aria-label="La pièce à renforcer">
        {#each pieces as l (l.id)}
          <option value={l.id}>{l.nom}</option>
        {/each}
      </select>

      <!-- La question. Elle change ce qu'on montre et ce qu'on consomme. -->
      <div class="origine" role="group" aria-label="Qui est à l'origine du renforcement">
        <span class="detail">Qui s'en charge ?</span>
        <button
          type="button"
          class="pilule"
          aria-pressed={origine === 'moi'}
          onclick={() => (origine = 'moi')}
        >
          Moi
        </button>
        <button
          type="button"
          class="pilule"
          aria-pressed={origine === 'autre'}
          onclick={() => (origine = 'autre')}
        >
          Un autre
        </button>
      </div>
      {#if origine === 'autre'}
        <p class="detail">
          Ses Capacités, son Matériel, son Atelier : tout est proposé, et rien
          ne part de votre sac. Ce que la pièce refuse d'elle-même reste refusé.
        </p>
      {:else}
        <p class="detail">
          Ce que votre Classe et votre sac permettent, et rien d'autre.
        </p>
      {/if}

      <!-- Ce qui est déjà posé se défait ici : le Rituel n'apparaît plus dans
           les offres, son quota étant atteint. -->
      {#if piece}
        {#each piece.renforts as r (r.source + r.sens)}
          <span class="offre">
            <span class="nom">{r.dit}</span>
            <button
              type="button"
              class="pilule discret"
              onclick={() => renforcer(cible, r.source, r.sens)}
            >
              Défaire
            </button>
          </span>
        {/each}
        {#each piece.ameliorations as a (a.id)}
          <span class="offre">
            <span class="nom">
              {a.nom}
              <span class="detail">{a.dit}</span>
            </span>
            <button type="button" class="pilule discret" onclick={() => monter(cible, a.id)}>
              Démonter
            </button>
          </span>
        {/each}
      {/if}

      {#if voies.length === 0 && origine === 'moi'}
        <p class="vide">
          Rien que vous puissiez faire vous-même sur cette pièce. Un autre le
          peut peut-être : changez l'origine.
        </p>
      {/if}

      {#each voies as v (v.id)}
        <section class="voie">
          <h4>{v.nom}</h4>
          <p class="detail">{v.resume}</p>
          {#each v.offres as g (g.id)}
            <span class="offre" class:hors={g.refus !== null}>
              <span class="nom">
                {g.nom}
                <span class="detail">{g.dit}</span>
                {#if g.refus}<span class="manque">{g.refus}</span>{/if}
              </span>
              {#if g.refus === null}
                {#if g.valeur > 0}
                  <button type="button" class="pilule" onclick={() => renforcer(cible, g.id, 1)}>
                    Octroyer <span class="data">+{g.valeur}</span>
                  </button>
                  <button type="button" class="pilule" onclick={() => renforcer(cible, g.id, -1)}>
                    Retirer <span class="data">−{g.valeur}</span>
                  </button>
                {:else if v.id === 'veredium'}
                  <button type="button" class="pilule" onclick={() => monter(cible, g.id)}>
                    Monter
                  </button>
                {:else if origine === 'autre'}
                  <!-- Son Matériel, pas le mien : la Propriété arrive, rien ne part. -->
                  <button type="button" class="pilule" onclick={() => attribuer(cible, g.id)}>
                    Attribuée
                  </button>
                {:else}
                  <button type="button" class="pilule" onclick={() => ameliorer(cible, g.id, true)}>
                    Test réussi
                  </button>
                  <button
                    type="button"
                    class="pilule discret"
                    onclick={() => ameliorer(cible, g.id, false)}
                  >
                    Raté
                  </button>
                {/if}
              {/if}
            </span>
          {/each}
        </section>
      {/each}
    {/if}
  </div>
</div>

<style>
  .corps {
    display: grid;
    gap: calc(var(--pas) * 2);
    padding: calc(var(--pas) * 3);
  }
  .origine {
    display: flex;
    align-items: center;
    gap: var(--pas);
    flex-wrap: wrap;
  }
  .voie {
    border-top: 1px solid var(--line);
    padding-top: calc(var(--pas) * 1.5);
    display: grid;
    gap: var(--pas);
  }
  .voie h4 {
    font-family: 'Cinzel', serif;
    font-weight: normal;
    font-size: 0.85rem;
    margin: 0;
  }
  .offre {
    display: flex;
    align-items: center;
    gap: var(--pas);
    flex-wrap: wrap;
  }
  .offre .nom {
    flex: 1;
    display: grid;
    min-width: 12rem;
  }
  /* Refusé par la pièce elle-même : on le montre, et on ne propose rien. */
  .offre.hors .nom {
    opacity: 0.65;
  }
</style>
